import { jsPDF } from "jspdf";

const CI = {
  red: "#6d0000" as const,
  teal: "#115751" as const,
  black: "#000000" as const,
  gray: "#555555" as const,
  lightGray: "#f2f2f2" as const,
  white: "#ffffff" as const,
  border: "#e0e0e0" as const,
};

function hexToRgb(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

function scoreColor(pct: number): string {
  if (pct < 40) return "#dc2626";
  if (pct < 70) return "#d97706";
  return "#16a34a";
}

function drawProgressBar(doc: jsPDF, x: number, y: number, width: number, height: number, pct: number, color: string) {
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(x, y, width, height, "F");
  const fillW = Math.max(0, Math.min(1, pct / 100)) * width;
  if (fillW > 0) {
    doc.setFillColor(...hexToRgb(color));
    doc.rect(x, y, fillW, height, "F");
  }
}

function addHeader(doc: jsPDF, title: string, subtitle: string, url: string) {
  const pw = doc.internal.pageSize.getWidth();
  doc.setFillColor(...hexToRgb(CI.black));
  doc.rect(0, 0, pw, 28, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text("WERBEAGENTUR LARISSA GIRENKO", 14, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(180, 180, 180);
  doc.text("Dein Schlüssel zum Marketingerfolg", 14, 18);
  doc.setFillColor(...hexToRgb(CI.red));
  doc.rect(0, 28, pw, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text(title, 14, 41);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(255, 200, 200);
  doc.text(subtitle, pw - 14, 41, { align: "right" });
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(0, 50, pw, 10, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...hexToRgb(CI.gray));
  doc.text(`Analysierte Website: ${url}`, 14, 57);
  const dateStr = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  doc.text(`Erstellt am: ${dateStr}`, pw - 14, 57, { align: "right" });
}

function addFooter(doc: jsPDF, pageNum: number, totalPages: number) {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...hexToRgb(CI.border));
  doc.setLineWidth(0.3);
  doc.line(14, ph - 14, pw - 14, ph - 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...hexToRgb(CI.gray));
  doc.text("© 2026 WERBEAGENTUR Larissa Girenko · barometer.werbeagentur.me", 14, ph - 8);
  doc.text(`Seite ${pageNum} / ${totalPages}`, pw - 14, ph - 8, { align: "right" });
}

// ─── SENSORIK PDF ─────────────────────────────────────────────────────────────
// Sensorik-Analyzer gibt: scores[key] = { label, icon, percent, score, max, criteria: [{label, percent, score, max}] }
// reifegrad = { name, desc }
export function generateSensorikPdf(data: Record<string, unknown>) {
  const d = data as any;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();

  addHeader(doc, "SENSORIK-BAROMETER™", "Website-Sensorik-Analyse", d.url || "");
  let y = 68;

  const sc = d.total_score ?? 0;
  const scColor = scoreColor(sc);
  doc.setFillColor(...hexToRgb(CI.black));
  doc.rect(14, y, 80, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`${sc}`, 54, y + 24, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  doc.text("von 100 Punkten", 54, y + 32, { align: "center" });

  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(100, y, pw - 114, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...hexToRgb(CI.red));
  // reifegrad.name (Sensorik) oder reifegrad.label (Fallback)
  const rgName = d.reifegrad?.name ?? d.reifegrad?.label ?? "";
  doc.text(rgName, 106, y + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...hexToRgb(CI.black));
  const rgDesc = d.reifegrad?.desc ?? "";
  const lines = doc.splitTextToSize(rgDesc, pw - 120);
  doc.text(lines.slice(0, 3), 106, y + 20);
  y += 50;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(CI.black));
  doc.text("ANALYSE NACH DIMENSIONEN", 14, y);
  doc.setDrawColor(...hexToRgb(CI.red));
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 80, y + 2);
  y += 8;

  const scores = d.scores ?? {};
  Object.values(scores).forEach((dim: any) => {
    if (y > 255) { doc.addPage(); y = 20; }
    const pct = dim.percent ?? (dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0);
    const col = scoreColor(pct);
    doc.setFillColor(...hexToRgb(CI.black));
    doc.rect(14, y, pw - 28, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.white));
    doc.text(`${dim.icon ?? ""} ${dim.label ?? ""}`, 17, y + 5.5);
    doc.setTextColor(...hexToRgb(col));
    doc.text(`${dim.score}/${dim.max}`, pw - 17, y + 5.5, { align: "right" });
    y += 8;
    drawProgressBar(doc, 14, y, pw - 28, 3, pct, col);
    y += 6;

    (dim.criteria ?? []).forEach((c: any) => {
      if (y > 265) { doc.addPage(); y = 20; }
      const cp = c.percent ?? (c.max > 0 ? Math.round((c.score / c.max) * 100) : 0);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`- ${c.label}`, 18, y + 3.5);
      drawProgressBar(doc, pw - 50, y + 1, 30, 2, cp, scoreColor(cp));
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(scoreColor(cp)));
      doc.text(`${c.score}/${c.max}`, pw - 16, y + 3.5, { align: "right" });
      y += 6;
    });
    y += 3;
  });

  if ((d.optimierungen ?? []).length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("PRIORISIERTE OPTIMIERUNGSEMPFEHLUNGEN", 14, y);
    doc.setDrawColor(...hexToRgb(CI.red));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 110, y + 2);
    y += 10;

    d.optimierungen.slice(0, 10).forEach((opt: any, i: number) => {
      const isHoch = opt.prioritaet === "hoch" || opt.prioritaet === "kritisch";
      const barColor = isHoch ? CI.red : (opt.prioritaet === "niedrig" ? "#4f46e5" : "#b45309");
      const prioritaetLabel = isHoch ? "!! HOCH" : (opt.prioritaet === "niedrig" ? "◦ NIEDRIG" : "-- MITTEL");

      doc.setFontSize(7);
      const beschreibung = opt.beschreibung ?? opt.tipp ?? "";
      const beschrLines = doc.splitTextToSize(beschreibung, pw - 42);

      const schritte: string[] = Array.isArray(opt.schritte) ? opt.schritte : [];
      const schritteLines: string[][] = schritte.map((s: string) => doc.splitTextToSize(`• ${s}`, pw - 46));
      const schritteLineCount = schritteLines.reduce((sum, l) => sum + l.length, 0);

      const beispiel = opt.beispiel ?? "";
      const beispielLines = beispiel ? doc.splitTextToSize(`Beispiel: ${beispiel}`, pw - 42) : [];

      const ergebnis = opt.erwartetes_ergebnis ?? "";
      const ergebnisLines = ergebnis ? doc.splitTextToSize(`Erwartetes Ergebnis: ${ergebnis}`, pw - 42) : [];

      const hasAufwand = opt.aufwand || opt.wirkung;
      const blockHeight =
        16 +
        (beschrLines.length * 3.5) +
        (schritteLineCount > 0 ? 6 + schritteLineCount * 3.5 : 0) +
        (beispielLines.length > 0 ? 5 + beispielLines.length * 3.5 : 0) +
        (ergebnisLines.length > 0 ? 5 + ergebnisLines.length * 3.5 : 0) +
        (hasAufwand ? 6 : 0);

      if (y + blockHeight > 270) { doc.addPage(); y = 20; }

      doc.setFillColor(...hexToRgb(barColor));
      doc.rect(14, y, 2, blockHeight, "F");
      doc.setFillColor(...hexToRgb(CI.lightGray));
      doc.rect(16, y, pw - 30, blockHeight, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`${i + 1}. ${opt.titel ?? opt.kriterium ?? ""}`, 20, y + 6);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(barColor));
      doc.text(prioritaetLabel, pw - 16, y + 6, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...hexToRgb(CI.gray));
      doc.text(`Dimension: ${opt.dimension ?? "\u2013"}`, 20, y + 11);

      let textY = y + 16;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(beschrLines, 20, textY);
      textY += beschrLines.length * 3.5;

      if (schritteLineCount > 0) {
        textY += 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.teal));
        doc.text("Schritt-für-Schritt:", 20, textY);
        textY += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.black));
        schritteLines.forEach((lines) => { doc.text(lines, 22, textY); textY += lines.length * 3.5; });
      }

      if (beispielLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#4f46e5"));
        doc.text(beispielLines, 20, textY);
        textY += beispielLines.length * 3.5;
      }

      if (ergebnisLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#16a34a"));
        doc.text(ergebnisLines, 20, textY);
        textY += ergebnisLines.length * 3.5;
      }

      if (hasAufwand) {
        textY += 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        const zeitInfo = opt.zeitaufwand ? ` | Zeit: ${opt.zeitaufwand}` : "";
        doc.text(`Aufwand: ${opt.aufwand ?? "\u2013"} | Wirkung: ${opt.wirkung ?? "\u2013"}${zeitInfo}`, 20, textY);
      }

      y += blockHeight + 4;
    });
  }

  if (y > 250) { doc.addPage(); y = 20; }
  y += 5;
  doc.setFillColor(...hexToRgb(CI.teal));
  doc.rect(14, y, pw - 28, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text("Möchtest du deinen Sensorik-Score verbessern?", pw / 2, y + 9, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Kontaktiere uns: werbeagentur.me · Dein Schlüssel zum Marketingerfolg", pw / 2, y + 16, { align: "center" });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); addFooter(doc, i, total); }
  const domain = (d.url ?? "website").replace(/https?:\/\//, "").replace(/\//g, "").slice(0, 30);
  doc.save(`Sensorik-Barometer_${domain}.pdf`);
}

// ─── LOKAL PDF ────────────────────────────────────────────────────────────────
// Lokal-Analyzer gibt: scores[key] = { score, max, details: {key: {found, score, max}} }
// reifegrad = { label, emoji, desc, color }
// optimierungen = [{ prioritaet, dimension, titel, beschreibung, schritte, aufwand, wirkung, zeitaufwand }]

const LOKAL_DIM_LABELS: Record<string, { label: string; emoji: string }> = {
  lokale_identitaet: { label: "Lokale Identität", emoji: "📍" },
  lokale_seo: { label: "Lokales SEO", emoji: "🔍" },
  google_business: { label: "Google Business", emoji: "🏢" },
  vertrauen: { label: "Vertrauen & Bewertungen", emoji: "⭐" },
  lokale_inhalte: { label: "Lokale Inhalte", emoji: "📝" },
  technisch: { label: "Technische Basis", emoji: "⚙️" },
  soziale_signale: { label: "Soziale Signale", emoji: "📱" },
  kontakt_conversion: { label: "Kontakt & Conversion", emoji: "📞" },
};

export function generateLokalPdf(data: Record<string, unknown>) {
  const d = data as any;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();

  addHeader(doc, "LOKAL-BAROMETER™", "Lokale Sichtbarkeits-Analyse", d.url || "");
  let y = 68;

  const sc = d.total_score ?? 0;
  const maxSc = d.max_score ?? 135;
  const scPct = maxSc > 0 ? Math.round((sc / maxSc) * 100) : 0;
  const scColor = scoreColor(scPct);

  doc.setFillColor(...hexToRgb(CI.black));
  doc.rect(14, y, 80, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(32);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`${sc}`, 54, y + 22, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  doc.text(`von ${maxSc} Punkten`, 54, y + 30, { align: "center" });

  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(100, y, pw - 114, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...hexToRgb(CI.red));
  // Lokal-Analyzer gibt reifegrad.label
  const rgName = d.reifegrad?.label ?? d.reifegrad?.name ?? "";
  doc.text(rgName, 106, y + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...hexToRgb(CI.black));
  const rgLines = doc.splitTextToSize(d.reifegrad?.desc ?? "", pw - 120);
  doc.text(rgLines.slice(0, 3), 106, y + 20);
  y += 50;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(CI.black));
  doc.text("ANALYSE NACH DIMENSIONEN", 14, y);
  doc.setDrawColor(...hexToRgb(CI.red));
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 80, y + 2);
  y += 8;

  const scores = d.scores ?? {};
  Object.entries(scores).forEach(([key, dim]: [string, any]) => {
    if (y > 255) { doc.addPage(); y = 20; }
    const meta = LOKAL_DIM_LABELS[key] || { label: key, emoji: "●" };
    const pct = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
    const col = scoreColor(pct);

    doc.setFillColor(...hexToRgb(CI.black));
    doc.rect(14, y, pw - 28, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.white));
    doc.text(`${meta.emoji} ${meta.label}`, 17, y + 5.5);
    doc.setTextColor(...hexToRgb(col));
    doc.text(`${dim.score}/${dim.max} (${pct}%)`, pw - 17, y + 5.5, { align: "right" });
    y += 8;
    drawProgressBar(doc, 14, y, pw - 28, 3, pct, col);
    y += 6;

    // Details aus dem Analyzer (found/score/max pro Kriterium)
    const details = dim.details ?? {};
    Object.entries(details).forEach(([detailKey, dv]: [string, any]) => {
      if (y > 265) { doc.addPage(); y = 20; }
      const isPos = dv?.found === true || (typeof dv?.score === "number" && dv.score > 0);
      const detailLabel = detailKey.replace(/_/g, " ");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(isPos ? "#16a34a" : "#dc2626"));
      doc.text(isPos ? "+" : "x", 18, y + 3.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(detailLabel, 24, y + 3.5);
      if (typeof dv?.score === "number" && typeof dv?.max === "number") {
        doc.setTextColor(...hexToRgb(CI.gray));
        doc.text(`${dv.score}/${dv.max}`, pw - 17, y + 3.5, { align: "right" });
      }
      y += 5.5;
    });
    y += 4;
  });

  const empf = d.optimierungen ?? d.empfehlungen ?? [];
  if (empf.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("OPTIMIERUNGSEMPFEHLUNGEN", 14, y);
    doc.setDrawColor(...hexToRgb(CI.red));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 90, y + 2);
    y += 10;

    empf.slice(0, 10).forEach((opt: any, i: number) => {
      const isHoch = opt.prioritaet === "hoch" || opt.prioritaet === "kritisch";
      const barColor = isHoch ? CI.red : (opt.prioritaet === "niedrig" ? "#4f46e5" : "#b45309");
      const prioritaetLabel = isHoch ? "!! HOCH" : (opt.prioritaet === "niedrig" ? "◦ NIEDRIG" : "-- MITTEL");

      // Texte vorbereiten
      doc.setFontSize(7);
      const beschreibung = opt.beschreibung ?? opt.massnahme ?? opt.tipp ?? "";
      const beschrLines = doc.splitTextToSize(beschreibung, pw - 42);

      // Schritte vorbereiten
      const schritte: string[] = Array.isArray(opt.schritte) ? opt.schritte : [];
      const schritteLines: string[][] = schritte.map((s: string) =>
        doc.splitTextToSize(`• ${s}`, pw - 46)
      );
      const schritteLineCount = schritteLines.reduce((sum, l) => sum + l.length, 0);

      // Beispiel
      const beispiel = opt.beispiel ?? "";
      const beispielLines = beispiel ? doc.splitTextToSize(`Beispiel: ${beispiel}`, pw - 42) : [];

      // Erwartetes Ergebnis
      const ergebnis = opt.erwartetes_ergebnis ?? "";
      const ergebnisLines = ergebnis ? doc.splitTextToSize(`Erwartetes Ergebnis: ${ergebnis}`, pw - 42) : [];

      const hasAufwand = opt.aufwand || opt.wirkung;
      const blockHeight =
        16 +
        (beschrLines.length * 3.5) +
        (schritteLineCount > 0 ? 6 + schritteLineCount * 3.5 : 0) +
        (beispielLines.length > 0 ? 5 + beispielLines.length * 3.5 : 0) +
        (ergebnisLines.length > 0 ? 5 + ergebnisLines.length * 3.5 : 0) +
        (hasAufwand ? 6 : 0);

      if (y + blockHeight > 270) { doc.addPage(); y = 20; }

      // Farbbalken links
      doc.setFillColor(...hexToRgb(barColor));
      doc.rect(14, y, 2, blockHeight, "F");
      // Hintergrund
      doc.setFillColor(...hexToRgb(CI.lightGray));
      doc.rect(16, y, pw - 30, blockHeight, "F");

      // Titel
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`${i + 1}. ${opt.titel ?? opt.kriterium ?? ""}`, 20, y + 6);

      // Priorität rechts
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(barColor));
      doc.text(prioritaetLabel, pw - 16, y + 6, { align: "right" });

      // Dimension
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...hexToRgb(CI.gray));
      doc.text(`Dimension: ${opt.dimension ?? "\u2013"}`, 20, y + 11);

      let textY = y + 16;

      // Beschreibungstext
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(beschrLines, 20, textY);
      textY += beschrLines.length * 3.5;

      // Schritte
      if (schritteLineCount > 0) {
        textY += 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.teal));
        doc.text("Schritt-für-Schritt:", 20, textY);
        textY += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.black));
        schritteLines.forEach((lines) => {
          doc.text(lines, 22, textY);
          textY += lines.length * 3.5;
        });
      }

      // Beispiel
      if (beispielLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#4f46e5"));
        doc.text(beispielLines, 20, textY);
        textY += beispielLines.length * 3.5;
      }

      // Erwartetes Ergebnis
      if (ergebnisLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#16a34a"));
        doc.text(ergebnisLines, 20, textY);
        textY += ergebnisLines.length * 3.5;
      }

      // Aufwand/Wirkung
      if (hasAufwand) {
        textY += 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        const zeitInfo = opt.zeitaufwand ? ` | Zeit: ${opt.zeitaufwand}` : "";
        doc.text(`Aufwand: ${opt.aufwand ?? "\u2013"} | Wirkung: ${opt.wirkung ?? "\u2013"}${zeitInfo}`, 20, textY);
      }

      y += blockHeight + 4;
    });
  }

  if (y > 250) { doc.addPage(); y = 20; }
  y += 5;
  doc.setFillColor(...hexToRgb(CI.teal));
  doc.rect(14, y, pw - 28, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text("Lokale Sichtbarkeit professionell steigern?", pw / 2, y + 9, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Kontaktiere uns: werbeagentur.me · Dein Schlüssel zum Marketingerfolg", pw / 2, y + 16, { align: "center" });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); addFooter(doc, i, total); }
  const domain = (d.url ?? "website").replace(/https?:\/\//, "").replace(/\//g, "").slice(0, 30);
  doc.save(`Lokal-Barometer_${domain}.pdf`);
}

// ─── GEO PDF ──────────────────────────────────────────────────────────────────
// GEO-Analyzer gibt: scores[key] = { score, max, details: {key: {found, score, max}} }
// reifegrad = { label, emoji, desc, color }
// optimierungen = [{ prioritaet, dimension, titel, beschreibung, warum, schritte, aufwand, zeitaufwand, wirkung, tools }]

const GEO_DIM_LABELS: Record<string, { label: string; emoji: string }> = {
  strukturierte_daten: { label: "Strukturierte Daten", emoji: "🔖" },
  eeat_signale: { label: "E-E-A-T Signale", emoji: "🎓" },
  content_tiefe: { label: "Content-Tiefe", emoji: "📚" },
  technische_zugaenglichkeit: { label: "Technische Zugänglichkeit", emoji: "⚙️" },
  zitierfahiger_content: { label: "Zitierfähiger Content", emoji: "💬" },
  marken_autoritaet: { label: "Marken-Autorität", emoji: "🏅" },
  frische_aktualitaet: { label: "Frische & Aktualität", emoji: "🗓" },
  prompt_relevanz: { label: "Prompt-Relevanz", emoji: "❓" },
};

export function generateGeoPdf(data: Record<string, unknown>) {
  const d = data as any;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();

  addHeader(doc, "GEO-BAROMETER™", "KI-Sichtbarkeits-Analyse (Generative Engine Optimization)", d.url || d.domain || "");
  let y = 68;

  const sc = d.total_score ?? 0;
  const maxSc = d.max_score ?? 127; // Wikipedia-Gewichtung reduziert: 130 → 127
  const scPct = maxSc > 0 ? Math.round((sc / maxSc) * 100) : 0;
  const scColor = scoreColor(scPct);

  doc.setFillColor(...hexToRgb(CI.black));
  doc.rect(14, y, 80, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(32);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`${sc}`, 54, y + 22, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  doc.text(`von ${maxSc} Punkten`, 54, y + 30, { align: "center" });

  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(100, y, pw - 114, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...hexToRgb(CI.red));
  // GEO-Analyzer gibt reifegrad.label
  const rgName = d.reifegrad?.label ?? d.reifegrad?.name ?? "";
  doc.text(rgName, 106, y + 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...hexToRgb(CI.black));
  const rgLines = doc.splitTextToSize(d.reifegrad?.desc ?? "", pw - 120);
  doc.text(rgLines.slice(0, 3), 106, y + 20);
  y += 50;

  // KI-Sichtbarkeit Einschätzung (basierend auf Score)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.black));
  doc.text("KI-PLATTFORMEN EINSCHÄTZUNG", 14, y);
  doc.setDrawColor(...hexToRgb(CI.red));
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 85, y + 2);
  y += 8;

  const platforms = [
    { name: "ChatGPT (OpenAI)", pct: scPct },
    { name: "Perplexity", pct: Math.max(0, scPct - 5) },
    { name: "Google AI Overviews", pct: Math.max(0, scPct - 3) },
    { name: "Bing Copilot", pct: Math.max(0, scPct - 8) },
  ];
  platforms.forEach((p) => {
    if (y > 265) { doc.addPage(); y = 20; }
    const col = p.pct >= 60 ? "#16a34a" : "#dc2626";
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text(p.name, 14, y + 3.5);
    drawProgressBar(doc, 70, y + 1, 80, 4, p.pct, col);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(col));
    doc.text(`${p.pct >= 60 ? "Sichtbar" : "Kaum sichtbar"} (${p.pct}%)`, pw - 16, y + 4, { align: "right" });
    y += 8;
  });
  y += 5;

  // Dimensionen
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(CI.black));
  doc.text("ANALYSE NACH DIMENSIONEN", 14, y);
  doc.setDrawColor(...hexToRgb(CI.red));
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 80, y + 2);
  y += 8;

  const scores = d.scores ?? {};
  Object.entries(scores).forEach(([key, dim]: [string, any]) => {
    if (y > 255) { doc.addPage(); y = 20; }
    const meta = GEO_DIM_LABELS[key] || { label: key, emoji: "●" };
    const pct = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
    const col = scoreColor(pct);

    doc.setFillColor(...hexToRgb(CI.black));
    doc.rect(14, y, pw - 28, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.white));
    doc.text(`${meta.emoji} ${meta.label}`, 17, y + 5.5);
    doc.setTextColor(...hexToRgb(col));
    doc.text(`${dim.score}/${dim.max} (${pct}%)`, pw - 17, y + 5.5, { align: "right" });
    y += 8;
    drawProgressBar(doc, 14, y, pw - 28, 3, pct, col);
    y += 6;

    const details = dim.details ?? {};
    Object.entries(details).forEach(([detailKey, dv]: [string, any]) => {
      if (y > 265) { doc.addPage(); y = 20; }
      const isPos = dv?.found === true || (typeof dv?.score === "number" && dv.score > 0);
      const detailLabel = detailKey.replace(/_/g, " ");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(isPos ? "#16a34a" : "#dc2626"));
      doc.text(isPos ? "+" : "x", 18, y + 3.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(detailLabel, 24, y + 3.5);
      if (typeof dv?.score === "number" && typeof dv?.max === "number") {
        doc.setTextColor(...hexToRgb(CI.gray));
        doc.text(`${dv.score}/${dv.max}`, pw - 17, y + 3.5, { align: "right" });
      }
      y += 5.5;
    });
    y += 4;
  });

  // Optimierungen (GEO-Analyzer gibt d.optimierungen)
  const empf = d.optimierungen ?? d.empfehlungen ?? [];
  if (empf.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("GEO-OPTIMIERUNGSEMPFEHLUNGEN", 14, y);
    doc.setDrawColor(...hexToRgb(CI.red));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 95, y + 2);
    y += 10;

    empf.slice(0, 10).forEach((opt: any, i: number) => {
      const isHoch = opt.prioritaet === "hoch" || opt.prioritaet === "kritisch";
      const barColor = opt.prioritaet === "kritisch" ? "#ef4444" : isHoch ? CI.red : "#b45309";

      // Vollständigen Beschreibungstext umbrechen
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      const beschreibung = opt.beschreibung ?? "";
      const descLines = doc.splitTextToSize(beschreibung, pw - 42);
      // Aufwand/Wirkung Zeile
      const hasAufwand = opt.aufwand || opt.wirkung;
      const aufwandLine = hasAufwand ? `Aufwand: ${opt.aufwand ?? "\u2013"} | Wirkung: ${opt.wirkung ?? "\u2013"} | ${opt.zeitaufwand ?? ""}` : "";
      // Warum-Text (falls vorhanden)
      const warumText = opt.warum ?? "";
      const warumLines = warumText ? doc.splitTextToSize(`Warum: ${warumText}`, pw - 42) : [];
      // Schritte (falls vorhanden)
      const schritte = Array.isArray(opt.schritte) ? opt.schritte : [];
      const schritteLines = schritte.map((s: string, idx: number) => `${idx + 1}. ${s}`);
      const schritteWrapped = schritteLines.flatMap((line: string) => doc.splitTextToSize(line, pw - 46));

      // Blockhöhe berechnen
      const blockHeight = 14 + (descLines.length * 3.2) + (hasAufwand ? 5 : 0) + (warumLines.length > 0 ? warumLines.length * 3.2 + 3 : 0) + (schritteWrapped.length > 0 ? schritteWrapped.length * 3.2 + 5 : 0);

      if (y + blockHeight > 270) { doc.addPage(); y = 20; }

      // Farbbalken links
      doc.setFillColor(...hexToRgb(barColor));
      doc.rect(14, y, 2, blockHeight, "F");
      // Hintergrund
      doc.setFillColor(...hexToRgb(CI.lightGray));
      doc.rect(16, y, pw - 30, blockHeight, "F");

      // Titel
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`${i + 1}. ${opt.titel ?? ""}`, 20, y + 5);

      // Priorität rechts
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(barColor));
      doc.text(opt.prioritaet?.toUpperCase() ?? "", pw - 16, y + 5, { align: "right" });

      // Dimension
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...hexToRgb(CI.gray));
      doc.text(`Dimension: ${opt.dimension ?? "\u2013"}`, 20, y + 10);

      // Beschreibungstext (vollständig)
      let textY = y + 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(descLines, 20, textY);
      textY += descLines.length * 3.2;

      // Warum (falls vorhanden)
      if (warumLines.length > 0) {
        textY += 2;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#4b5563"));
        doc.text(warumLines, 20, textY);
        textY += warumLines.length * 3.2;
      }

      // Schritte (falls vorhanden)
      if (schritteWrapped.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.teal));
        doc.text("Schritt-für-Schritt:", 20, textY);
        textY += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#374151"));
        doc.text(schritteWrapped, 22, textY);
        textY += schritteWrapped.length * 3.2;
      }

      // Beispiel (falls vorhanden)
      const beispiel = opt.beispiel ?? "";
      if (beispiel) {
        const beispielLines = doc.splitTextToSize(`Beispiel: ${beispiel}`, pw - 42);
        textY += 3;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#4f46e5"));
        doc.text(beispielLines, 20, textY);
        textY += beispielLines.length * 3.2;
      }

      // Erwartetes Ergebnis (falls vorhanden)
      const ergebnis = opt.erwartetes_ergebnis ?? "";
      if (ergebnis) {
        const ergebnisLines = doc.splitTextToSize(`Erwartetes Ergebnis: ${ergebnis}`, pw - 42);
        textY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#16a34a"));
        doc.text(ergebnisLines, 20, textY);
        textY += ergebnisLines.length * 3.2;
      }

      // Aufwand/Wirkung
      if (hasAufwand) {
        textY += 1;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        doc.text(aufwandLine, 20, textY);
      }

      y += blockHeight + 4;
    });
  }

  if (y > 250) { doc.addPage(); y = 20; }
  y += 5;
  doc.setFillColor(...hexToRgb(CI.teal));
  doc.rect(14, y, pw - 28, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text("KI-Sichtbarkeit professionell ausbauen?", pw / 2, y + 9, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Kontaktiere uns: werbeagentur.me · Dein Schlüssel zum Marketingerfolg", pw / 2, y + 16, { align: "center" });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); addFooter(doc, i, total); }
  const domain = (d.url ?? d.domain ?? "website").replace(/https?:\/\//, "").replace(/\//g, "").slice(0, 30);
  doc.save(`GEO-Barometer_${domain}.pdf`);
}

// ─── SEO PDF ──────────────────────────────────────────────────────────────────
// SEO-Analyzer gibt: dimensions[{label, icon, score, max, beschreibung, details:{key:{score,max,found,issues,...}}}]
// Details enthalten u.a.: keyword_fokus{erkanntes_keyword,im_title,in_h1,in_meta,in_text}, zielgruppen_fragen{beantwortet[],fehlend[]}, textlaenge{char_count,word_count,min_1800_chars}
// empfehlungen = [{dimension, icon, prioritaet, kriterium, massnahme}]
// load_time_ms, total_score, max_score, percent, grade, domain, url

export function generateSeoPdf(data: Record<string, unknown>) {
  const d = data as any;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();

  addHeader(doc, "SEO-BAROMETER™", "SEO-Grundlagen-Check", d.url || d.domain || "");
  let y = 68;

  const sc = d.total_score ?? 0;
  const maxSc = d.max_score ?? 100;
  const pct = d.percent ?? (maxSc > 0 ? Math.round((sc / maxSc) * 100) : 0);
  const grade = d.grade ?? "–";
  const scColor = scoreColor(pct);

  // Score-Box
  doc.setFillColor(...hexToRgb(CI.black));
  doc.rect(14, y, 80, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`${sc}`, 54, y + 22, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  doc.text(`von ${maxSc} Punkten`, 54, y + 30, { align: "center" });

  // Note + Hinweis
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(100, y, pw - 114, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`Note: ${grade}`, 106, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...hexToRgb(CI.gray));
  const noteText = pct >= 70 ? "Gute SEO-Grundlagen. Feinschliff empfohlen." : pct >= 45 ? "Ausbaufähige SEO. Handlungsbedarf vorhanden." : "Kritische SEO-Mängel. Sofortmaßnahmen nötig.";
  doc.text(noteText, 106, y + 24);
  doc.setFontSize(6.5);
  doc.setTextColor(...hexToRgb(CI.gray));
  doc.text("Hinweis: SEO-Grundlagen-Check (On-Page & Technik).", 106, y + 32);
  doc.text("Backlinks, Domain Authority & Rankings nicht enthalten.", 106, y + 37);
  y += 50;

  // Ladezeit / Core Web Vitals
  const loadMs = d.load_time_ms ?? 0;
  const cwv = d.core_web_vitals ?? null;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.black));
  doc.text("LADEGESCHWINDIGKEIT & CORE WEB VITALS", 14, y);
  doc.setDrawColor(...hexToRgb(CI.red));
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 110, y + 2);
  y += 8;

  if (cwv && cwv.available) {
    const vitals = [
      { label: "Performance Score", value: `${cwv.performance_score}/100`, good: cwv.performance_score >= 70 },
      { label: "LCP (Largest Contentful Paint)", value: `${(cwv.lcp_ms / 1000).toFixed(2)}s`, good: cwv.lcp_ms <= 2500 },
      { label: "CLS (Cumulative Layout Shift)", value: `${cwv.cls}`, good: cwv.cls <= 0.1 },
      { label: "INP (Interaction to Next Paint)", value: cwv.inp_ms > 0 ? `${cwv.inp_ms}ms` : "n/a", good: cwv.inp_ms <= 200 || cwv.inp_ms === 0 },
    ];
    vitals.forEach((v) => {
      if (y > 265) { doc.addPage(); y = 20; }
      const col = v.good ? "#16a34a" : "#dc2626";
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(v.label, 14, y + 3.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...hexToRgb(col));
      doc.text(v.value, pw - 14, y + 3.5, { align: "right" });
      y += 7;
    });
    doc.setFont("helvetica", "italic");
    doc.setFontSize(6.5);
    doc.setTextColor(...hexToRgb(CI.gray));
    doc.text("Quelle: Google PageSpeed Insights API", 14, y + 3);
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.black));
    const ltColor = loadMs <= 1500 ? "#16a34a" : loadMs <= 3000 ? "#ca8a04" : "#dc2626";
    doc.text("Gemessene Ladezeit:", 14, y + 3.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...hexToRgb(ltColor));
    doc.text(`${(loadMs / 1000).toFixed(2)}s`, pw - 14, y + 3.5, { align: "right" });
  }
  y += 12;

  // Dimensionen
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(CI.black));
  doc.text("ANALYSE NACH DIMENSIONEN", 14, y);
  doc.setDrawColor(...hexToRgb(CI.red));
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 80, y + 2);
  y += 8;

  const dimensions = d.dimensions ?? [];
  dimensions.forEach((dim: any) => {
    if (y > 255) { doc.addPage(); y = 20; }
    const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
    const col = scoreColor(dp);

    doc.setFillColor(...hexToRgb(CI.black));
    doc.rect(14, y, pw - 28, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.white));
    doc.text(`${dim.icon ?? ""} ${dim.label ?? ""}`, 17, y + 5.5);
    doc.setTextColor(...hexToRgb(col));
    doc.text(`${dim.score}/${dim.max} (${dp}%)`, pw - 17, y + 5.5, { align: "right" });
    y += 8;
    drawProgressBar(doc, 14, y, pw - 28, 3, dp, col);
    y += 6;

    const details = dim.details ?? {};
    Object.entries(details).forEach(([key, dv]: [string, any]) => {
      if (y > 265) { doc.addPage(); y = 20; }
      const isPos = dv?.found === true || (typeof dv?.score === "number" && dv.score > 0);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(isPos ? "#16a34a" : "#dc2626"));
      doc.text(isPos ? "+" : "x", 18, y + 3.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(key.replace(/_/g, " "), 24, y + 3.5);
      if (typeof dv?.score === "number" && typeof dv?.max === "number") {
        doc.setTextColor(...hexToRgb(CI.gray));
        doc.text(`${dv.score}/${dv.max}`, pw - 17, y + 3.5, { align: "right" });
      }
      y += 5.5;

      // Keyword-Fokus: erkanntes Keyword + Vorkommen anzeigen
      if (key === "keyword_fokus" && dv?.erkanntes_keyword) {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        const kwInfo = `Keyword: "${dv.erkanntes_keyword}" | Title: ${dv.im_title ? "+" : "x"} | H1: ${dv.in_h1 ? "+" : "x"} | Meta: ${dv.in_meta ? "+" : "x"} | Text: ${dv.in_text ? "+" : "x"}`;
        doc.text(kwInfo, 24, y + 3);
        y += 5;
      }

      // Zielgruppen-Fragen: beantwortet/fehlend anzeigen
      if (key === "zielgruppen_fragen" && (dv?.fehlend?.length > 0 || dv?.beantwortet?.length > 0)) {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        if (dv.fehlend?.length > 0) {
          doc.setTextColor(...hexToRgb("#dc2626"));
          const fehlendText = `Fehlend: ${dv.fehlend.join(", ")}`;
          const fehlendLines = doc.splitTextToSize(fehlendText, pw - 42);
          doc.text(fehlendLines, 24, y + 3);
          y += fehlendLines.length * 3.2 + 1;
        }
        if (dv.beantwortet?.length > 0) {
          if (y > 265) { doc.addPage(); y = 20; }
          doc.setTextColor(...hexToRgb("#16a34a"));
          const beantwortetText = `Beantwortet: ${dv.beantwortet.join(", ")}`;
          const beantwortetLines = doc.splitTextToSize(beantwortetText, pw - 42);
          doc.text(beantwortetLines, 24, y + 3);
          y += beantwortetLines.length * 3.2 + 1;
        }
      }

      // Textlaenge: Zeichenanzahl anzeigen
      if (key === "textlaenge" && dv?.char_count != null) {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(dv.min_1800_chars ? "#16a34a" : "#dc2626"));
        const tlInfo = `${dv.char_count.toLocaleString()} Zeichen${dv.word_count ? ` (${dv.word_count} Woerter)` : ""} ${dv.min_1800_chars ? "- Minimum erreicht" : "- unter 1.800 Zeichen"}`;
        doc.text(tlInfo, 24, y + 3);
        y += 5;
      }

      // Analytics/Tracking
      if (key === "analytics_tracking" && dv?.tool) {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(dv.tool === "keins" ? "#dc2626" : "#16a34a"));
        doc.text(`Tool: ${dv.tool}`, 24, y + 3);
        y += 5;
      }

      // URL-Qualitaet
      if (key === "url_qualitaet" && dv?.laenge != null) {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        doc.text(`Laenge: ${dv.laenge} Zeichen | Lesbar: ${dv.lesbar ? "+" : "x"} | Sonderzeichen: ${dv.sonderzeichen ? "x" : "+"}`, 24, y + 3);
        y += 5;
      }

      // Open Graph
      if (key === "open_graph") {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        doc.text(`og:title: ${dv?.og_title ? "+" : "x"} | og:description: ${dv?.og_description ? "+" : "x"} | og:image: ${dv?.og_image ? "+" : "x"}`, 24, y + 3);
        y += 5;
      }

      // Bildformate
      if (key === "bildformate" && dv?.formate_gefunden) {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        doc.text(`Formate: ${(dv.formate_gefunden || []).join(", ")} | WebP-Anteil: ${dv.webp_anteil ?? 0}%`, 24, y + 3);
        y += 5;
      }

      // Breadcrumbs
      if (key === "breadcrumbs") {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(dv?.schema_breadcrumb ? "#16a34a" : "#dc2626"));
        doc.text(`Schema BreadcrumbList: ${dv?.schema_breadcrumb ? "vorhanden" : "fehlt"}`, 24, y + 3);
        y += 5;
      }

      // hreflang
      if (key === "hreflang" && dv?.sprachen?.length > 0) {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#16a34a"));
        doc.text(`Sprachen: ${dv.sprachen.join(", ")}`, 24, y + 3);
        y += 5;
      }

      // Popups/Overlays
      if (key === "popups_overlays" && dv?.typ && dv.typ !== "keins") {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(dv.typ === "vollbild_overlay" ? "#dc2626" : "#d97706"));
        doc.text(`Erkannt: ${dv.typ.replace(/_/g, " ")}`, 24, y + 3);
        y += 5;
      }

      // Alt-Texte Statistik
      if (key === "alt_texte" && dv?.bilder_gesamt != null) {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        doc.text(`${dv.bilder_mit_alt ?? 0} von ${dv.bilder_gesamt} Bildern mit Alt-Text`, 24, y + 3);
        y += 5;
      }
    });
    y += 4;
  });

  // Empfehlungen
  const empf = d.empfehlungen ?? [];
  if (empf.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("SEO-OPTIMIERUNGSEMPFEHLUNGEN", 14, y);
    doc.setDrawColor(...hexToRgb(CI.red));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 100, y + 2);
    y += 10;

    empf.slice(0, 12).forEach((opt: any, i: number) => {
      const titel = opt.titel ?? opt.kriterium ?? "";
      const beschreibung = opt.beschreibung ?? opt.massnahme ?? opt.tipp ?? "";
      const isHoch = opt.prioritaet === "hoch" || opt.prioritaet === "kritisch";
      const barColor = isHoch ? CI.red : (opt.prioritaet === "niedrig" ? "#4f46e5" : "#b45309");
      const prioritaetLabel = isHoch ? "!! HOCH" : (opt.prioritaet === "niedrig" ? "◦ NIEDRIG" : "-- MITTEL");

      doc.setFontSize(7);
      const descLines = doc.splitTextToSize(beschreibung, pw - 42);

      const schritte: string[] = Array.isArray(opt.schritte) ? opt.schritte : [];
      const schritteLines: string[][] = schritte.map((s: string) => doc.splitTextToSize(`• ${s}`, pw - 46));
      const schritteLineCount = schritteLines.reduce((sum, l) => sum + l.length, 0);

      const beispiel = opt.beispiel ?? "";
      const beispielLines = beispiel ? doc.splitTextToSize(`Beispiel: ${beispiel}`, pw - 42) : [];

      const ergebnis = opt.erwartetes_ergebnis ?? "";
      const ergebnisLines = ergebnis ? doc.splitTextToSize(`Erwartetes Ergebnis: ${ergebnis}`, pw - 42) : [];

      const hasAufwand = opt.aufwand || opt.wirkung;
      const blockHeight =
        16 +
        (descLines.length * 3.5) +
        (schritteLineCount > 0 ? 6 + schritteLineCount * 3.5 : 0) +
        (beispielLines.length > 0 ? 5 + beispielLines.length * 3.5 : 0) +
        (ergebnisLines.length > 0 ? 5 + ergebnisLines.length * 3.5 : 0) +
        (hasAufwand ? 6 : 0);

      if (y + blockHeight > 270) { doc.addPage(); y = 20; }

      doc.setFillColor(...hexToRgb(barColor));
      doc.rect(14, y, 2, blockHeight, "F");
      doc.setFillColor(...hexToRgb(CI.lightGray));
      doc.rect(16, y, pw - 30, blockHeight, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`${i + 1}. ${titel}`, 20, y + 6);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(barColor));
      doc.text(prioritaetLabel, pw - 16, y + 6, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...hexToRgb(CI.gray));
      doc.text(`Dimension: ${opt.dimension ?? "\u2013"}`, 20, y + 11);

      let textY = y + 16;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(descLines, 20, textY);
      textY += descLines.length * 3.5;

      if (schritteLineCount > 0) {
        textY += 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.teal));
        doc.text("Schritt-für-Schritt:", 20, textY);
        textY += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.black));
        schritteLines.forEach((lines) => { doc.text(lines, 22, textY); textY += lines.length * 3.5; });
      }

      if (beispielLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#4f46e5"));
        doc.text(beispielLines, 20, textY);
        textY += beispielLines.length * 3.5;
      }

      if (ergebnisLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#16a34a"));
        doc.text(ergebnisLines, 20, textY);
        textY += ergebnisLines.length * 3.5;
      }

      if (hasAufwand) {
        textY += 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        const zeitInfo = opt.zeitaufwand ? ` | Zeit: ${opt.zeitaufwand}` : "";
        doc.text(`Aufwand: ${opt.aufwand ?? "\u2013"} | Wirkung: ${opt.wirkung ?? "\u2013"}${zeitInfo}`, 20, textY);
      }

      y += blockHeight + 4;
    });
  }

  // Disclaimer
  if (y > 250) { doc.addPage(); y = 20; }
  y += 5;
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(14, y, pw - 28, 16, "F");
  doc.setFont("helvetica", "italic");
  doc.setFontSize(6.5);
  doc.setTextColor(...hexToRgb(CI.gray));
  const disclaimer = "Hinweis: Dieser Report ist ein SEO-Grundlagen-Check (On-Page & technische SEO). Nicht enthalten: Backlink-Profil, Domain Authority, Keyword-Rankings, Suchvolumen. Für eine vollständige SEO-Strategie empfehlen wir eine individuelle Beratung.";
  const disclaimerLines = doc.splitTextToSize(disclaimer, pw - 36);
  doc.text(disclaimerLines, 20, y + 6);
  y += 22;

  // CTA
  doc.setFillColor(...hexToRgb(CI.teal));
  doc.rect(14, y, pw - 28, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text("SEO professionell ausbauen?", pw / 2, y + 9, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Kontaktiere uns: werbeagentur.me · Dein Schlüssel zum Marketingerfolg", pw / 2, y + 16, { align: "center" });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); addFooter(doc, i, total); }
  const domain = (d.url ?? d.domain ?? "website").replace(/https?:\/\//, "").replace(/\//g, "").slice(0, 30);
  doc.save(`SEO-Barometer_${domain}.pdf`);
}

// ─── Accessibility-Barometer PDF ─────────────────────────────────────────────
// data: { url, domain, total_score, max_score, prozent, grade, analyse_dauer,
//         dimensionen: [{name, punkte, max, details: [{text, ok}]}],
//         optimierungen: [{titel, beschreibung, prioritaet, dimension}],
//         wcag_level?, has_aria?, has_alt_texts?, keyboard_navigable? }

export function generateAccessibilityPdf(data: Record<string, unknown>) {
  const d = data as any;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();

  addHeader(doc, "ACCESSIBILITY-BAROMETER™", "WCAG 2.2 & European Accessibility Act", d.url || d.domain || "");
  let y = 68;

  const sc = d.total_score ?? 0;
  const maxSc = d.max_score ?? 100;
  const pct = d.prozent ?? (maxSc > 0 ? Math.round((sc / maxSc) * 100) : 0);
  const grade = d.grade ?? "–";
  const scColor = scoreColor(pct);

  // Score-Box
  doc.setFillColor(...hexToRgb(CI.black));
  doc.rect(14, y, 80, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`${sc}`, 54, y + 22, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  doc.text(`von ${maxSc} Punkten`, 54, y + 30, { align: "center" });

  // Note + Badges
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(100, y, pw - 114, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`Note: ${grade}`, 106, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...hexToRgb(CI.gray));
  const noteText = pct >= 70 ? "Gute Barrierefreiheit. Feinschliff empfohlen." : pct >= 45 ? "Ausbaufähig. Handlungsbedarf vorhanden." : "Kritische Mängel. Sofortmaßnahmen nötig.";
  doc.text(noteText, 106, y + 24);
  if (d.wcag_level) {
    doc.setFontSize(6.5);
    doc.text(`WCAG-Level: ${d.wcag_level}`, 106, y + 32);
  }
  y += 50;

  // Badges
  const badges = [
    d.has_aria && "ARIA-Rollen",
    d.has_alt_texts && "Alt-Texte",
    d.keyboard_navigable && "Tastaturnavigation",
  ].filter(Boolean) as string[];
  if (badges.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb("#059669"));
    doc.text(badges.join("  ·  "), 14, y);
    y += 8;
  }

  // Dimensionen
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(CI.black));
  doc.text("ANALYSE NACH DIMENSIONEN", 14, y);
  doc.setDrawColor(...hexToRgb(CI.red));
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 80, y + 2);
  y += 8;

  const dimensionen = d.dimensionen ?? [];
  dimensionen.forEach((dim: any) => {
    if (y > 255) { doc.addPage(); y = 20; }
    const dp = dim.max > 0 ? Math.round((dim.punkte / dim.max) * 100) : 0;
    const col = scoreColor(dp);

    doc.setFillColor(...hexToRgb(CI.black));
    doc.rect(14, y, pw - 28, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.white));
    // Nur den Namen anzeigen, kein Icon-URL als Text
    const dimLabel = String(dim.name ?? "").replace(/^\/manus-storage\/[^\s]+\s*/g, "");
    doc.text(dimLabel, 17, y + 5.5);
    doc.setTextColor(...hexToRgb(col));
    doc.text(`${dim.punkte}/${dim.max} (${dp}%)`, pw - 17, y + 5.5, { align: "right" });
    y += 8;
    drawProgressBar(doc, 14, y, pw - 28, 3, dp, col);
    y += 7;

    const details = dim.details ?? [];
    details.slice(0, 6).forEach((detail: any) => {
      if (y > 265) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(detail.ok ? "#16a34a" : "#dc2626"));
      doc.text(detail.ok ? "+" : "x", 18, y + 3.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(detail.text ?? "", 24, y + 3.5);
      y += 5.5;
    });
    y += 4;
  });

  // Optimierungen
  const opts = d.optimierungen ?? [];
  if (opts.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("OPTIMIERUNGSEMPFEHLUNGEN", 14, y);
    doc.setDrawColor(...hexToRgb(CI.red));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 90, y + 2);
    y += 10;

    opts.slice(0, 10).forEach((opt: any, i: number) => {
      const isHoch = opt.prioritaet === "hoch" || opt.prioritaet === "kritisch";
      const barColor = isHoch ? CI.red : (opt.prioritaet === "niedrig" ? "#4f46e5" : "#b45309");
      const prioritaetLabel = isHoch ? "!! HOCH" : (opt.prioritaet === "niedrig" ? "◦ NIEDRIG" : "-- MITTEL");

      doc.setFontSize(7);
      const beschreibung = opt.beschreibung ?? opt.hinweis ?? opt.tipp ?? "";
      const beschrLines = doc.splitTextToSize(beschreibung, pw - 42);

      const schritte: string[] = Array.isArray(opt.schritte) ? opt.schritte : [];
      const schritteLines: string[][] = schritte.map((s: string) => doc.splitTextToSize(`• ${s}`, pw - 46));
      const schritteLineCount = schritteLines.reduce((sum, l) => sum + l.length, 0);

      const beispiel = opt.beispiel ?? "";
      const beispielLines = beispiel ? doc.splitTextToSize(`Beispiel: ${beispiel}`, pw - 42) : [];

      const ergebnis = opt.erwartetes_ergebnis ?? "";
      const ergebnisLines = ergebnis ? doc.splitTextToSize(`Erwartetes Ergebnis: ${ergebnis}`, pw - 42) : [];

      const hasAufwand = opt.aufwand || opt.wirkung;
      const blockHeight =
        16 +
        (beschrLines.length * 3.5) +
        (schritteLineCount > 0 ? 6 + schritteLineCount * 3.5 : 0) +
        (beispielLines.length > 0 ? 5 + beispielLines.length * 3.5 : 0) +
        (ergebnisLines.length > 0 ? 5 + ergebnisLines.length * 3.5 : 0) +
        (hasAufwand ? 6 : 0);

      if (y + blockHeight > 270) { doc.addPage(); y = 20; }

      doc.setFillColor(...hexToRgb(barColor));
      doc.rect(14, y, 2, blockHeight, "F");
      doc.setFillColor(...hexToRgb(CI.lightGray));
      doc.rect(16, y, pw - 30, blockHeight, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`${i + 1}. ${opt.titel ?? opt.check ?? ""}`, 20, y + 6);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(barColor));
      doc.text(prioritaetLabel, pw - 16, y + 6, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...hexToRgb(CI.gray));
      doc.text(`Dimension: ${opt.dimension ?? "\u2013"}`, 20, y + 11);

      let textY = y + 16;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(beschrLines, 20, textY);
      textY += beschrLines.length * 3.5;

      if (schritteLineCount > 0) {
        textY += 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.teal));
        doc.text("Schritt-für-Schritt:", 20, textY);
        textY += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.black));
        schritteLines.forEach((lines) => { doc.text(lines, 22, textY); textY += lines.length * 3.5; });
      }

      if (beispielLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#4f46e5"));
        doc.text(beispielLines, 20, textY);
        textY += beispielLines.length * 3.5;
      }

      if (ergebnisLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#16a34a"));
        doc.text(ergebnisLines, 20, textY);
        textY += ergebnisLines.length * 3.5;
      }

      if (hasAufwand) {
        textY += 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        const zeitInfo = opt.zeitaufwand ? ` | Zeit: ${opt.zeitaufwand}` : "";
        doc.text(`Aufwand: ${opt.aufwand ?? "\u2013"} | Wirkung: ${opt.wirkung ?? "\u2013"}${zeitInfo}`, 20, textY);
      }

      y += blockHeight + 4;
    });
  }
  // Disclaimer
  if (y > 250) { doc.addPage(); y = 20; }
  y += 5;
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(14, y, pw - 28, 16, "F");
  doc.setFont("helvetica", "italic");
  doc.setFontSize(6.5);
  doc.setTextColor(...hexToRgb(CI.gray));
  const disclaimer = "Hinweis: Dieser Report ist ein technischer Accessibility-Check. Er ersetzt keine manuelle WCAG-Prüfung oder rechtliche Beratung. Für eine vollständige Barrierefreiheitsprüfung nach BITV 2.0 / EN 301 549 empfehlen wir eine individuelle Beratung.";
  const disclaimerLines = doc.splitTextToSize(disclaimer, pw - 36);
  doc.text(disclaimerLines, 20, y + 6);
  y += 22;

  // CTA
  doc.setFillColor(...hexToRgb("#4f46e5"));
  doc.rect(14, y, pw - 28, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text("Barrierefreiheit professionell umsetzen?", pw / 2, y + 9, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Kontaktiere uns: werbeagentur.me · Dein Schlüssel zum Marketingerfolg", pw / 2, y + 16, { align: "center" });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); addFooter(doc, i, total); }
  const domain = (d.url ?? d.domain ?? "website").replace(/https?:\/\//, "").replace(/\//g, "").slice(0, 30);
  doc.save(`Accessibility-Barometer_${domain}.pdf`);
}

// ─── Datenschutz-Barometer PDF ───────────────────────────────────────────────
// data: { url, domain, total_score, max_score, prozent, grade, analyse_dauer,
//         dimensionen: [{name, punkte, max, details: [{text, ok}]}],
//         optimierungen: [{titel, beschreibung, prioritaet, dimension}],
//         has_ssl?, has_impressum?, has_datenschutz?, has_cookie_banner? }

export function generateDatenschutzPdf(data: Record<string, unknown>) {
  const d = data as any;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();

  addHeader(doc, "DATENSCHUTZ-BAROMETER™", "DSGVO-Konformitätsprüfung", d.url || d.domain || "");
  let y = 68;

  const sc = d.total_score ?? 0;
  const maxSc = d.max_score ?? 100;
  const pct = d.prozent ?? (maxSc > 0 ? Math.round((sc / maxSc) * 100) : 0);
  const grade = d.grade ?? "–";
  const scColor = scoreColor(pct);

  // Score-Box
  doc.setFillColor(...hexToRgb(CI.black));
  doc.rect(14, y, 80, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`${sc}`, 54, y + 22, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  doc.text(`von ${maxSc} Punkten`, 54, y + 30, { align: "center" });

  // Note + Hinweis
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(100, y, pw - 114, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`Note: ${grade}`, 106, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...hexToRgb(CI.gray));
  const noteText = pct >= 70 ? "Gute DSGVO-Grundlagen. Feinschliff empfohlen." : pct >= 45 ? "Ausbaufähig. Handlungsbedarf vorhanden." : "Kritische Datenschutzmängel. Sofortmaßnahmen nötig.";
  doc.text(noteText, 106, y + 24);
  doc.setFontSize(6.5);
  doc.text("Technische Prüfung – kein Ersatz für Rechtsberatung.", 106, y + 32);
  y += 50;

  // Badges
  const badges = [
    d.has_ssl && "SSL/HTTPS",
    d.has_impressum && "Impressum",
    d.has_datenschutz && "Datenschutzerklaerung",
    d.has_cookie_banner && "Cookie-Banner",
  ].filter(Boolean) as string[];
  if (badges.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb("#059669"));
    doc.text(badges.join("  ·  "), 14, y);
    y += 8;
  }

  // Dimensionen
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(CI.black));
  doc.text("ANALYSE NACH DIMENSIONEN", 14, y);
  doc.setDrawColor(...hexToRgb(CI.red));
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 80, y + 2);
  y += 8;

  const dimensionen = d.dimensionen ?? [];
  dimensionen.forEach((dim: any) => {
    if (y > 255) { doc.addPage(); y = 20; }
    const dp = dim.max > 0 ? Math.round((dim.punkte / dim.max) * 100) : 0;
    const col = scoreColor(dp);

    doc.setFillColor(...hexToRgb(CI.black));
    doc.rect(14, y, pw - 28, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.white));
    doc.text(`${dim.icon ?? ""} ${dim.dimension ?? dim.name ?? ""}`, 17, y + 5.5);
    doc.setTextColor(...hexToRgb(col));
    doc.text(`${dim.punkte}/${dim.max} (${dp}%)`, pw - 17, y + 5.5, { align: "right" });
    y += 8;
    drawProgressBar(doc, 14, y, pw - 28, 3, dp, col);
    y += 7;

    const details = dim.details ?? [];
    details.slice(0, 6).forEach((detail: any) => {
      if (y > 265) { doc.addPage(); y = 20; }
      const isOk = detail.ok === true || detail.status === 'ok';
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(isOk ? "#16a34a" : "#dc2626"));
      doc.text(isOk ? "+" : "x", 18, y + 3.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(detail.text ?? detail.check ?? "", 24, y + 3.5);
      y += 5.5;
    });
    y += 4;
  });

  // Optimierungen
  // ── STAERKEN-SEKTION (was bereits gut gemacht wird) ──────────────────────────
  const allDetails: Array<{check: string; status: string; hinweis?: string; dimension: string}> = [];
  (d.dimensionen ?? []).forEach((dim: any) => {
    (dim.details ?? []).forEach((det: any) => {
      allDetails.push({
        check: det.check ?? det.text ?? '',
        status: det.status ?? (det.ok ? 'ok' : 'kritisch'),
        hinweis: det.hinweis,
        dimension: dim.dimension ?? dim.name ?? '',
      });
    });
  });
  const staerken = allDetails.filter(d => d.status === 'ok');

  // Positive Stärken-Lob-Texte pro Check
  const _staerkenLob: Record<string, string> = {
    'Impressum vorhanden': 'Dein Impressum ist vorhanden und erreichbar. Das ist die Grundvoraussetzung fuer die rechtliche Absicherung deiner Website gemaess §5 TMG.',
    'Datenschutzerklaerung verlinkt': 'Deine Datenschutzerklaerung ist verlinkt und erreichbar. Damit erfuellst du die Grundanforderung aus DSGVO Art. 13. Pruefe regelmaessig, ob alle Dienste (Google Analytics, Fonts, Maps etc.) darin aufgefuehrt sind.',
    'AGB vorhanden': 'Deine AGB sind vorhanden. Das schafft Rechtssicherheit bei Vertragsabschluessen und schuetzt dich vor Abmahnungen.',
    'Widerrufsrecht vorhanden': 'Dein Widerrufsrecht ist korrekt eingebunden. Das ist Pflicht bei Fernabsatzvertraegen gemaess §355 BGB und schuetzt deine Kunden.',
    'Cookie-Banner vorhanden': 'Dein Cookie-Banner ist vorhanden. Nutzer werden ueber Cookies informiert, bevor sie die Seite nutzen.',
    'Cookie-Kategorien unterschieden': 'Dein Cookie-Banner unterscheidet zwischen verschiedenen Cookie-Kategorien. Das entspricht den Anforderungen der DSGVO und gibt Nutzern echte Wahlmoeglichkeiten.',
    'Opt-in Zustimmung': 'Dein Cookie-Banner setzt auf aktive Zustimmung (Opt-in). Das ist DSGVO-konform und schuetzt dich vor Bussgeldern.',
    'HTTPS/SSL aktiv': 'Deine Website nutzt HTTPS/SSL-Verschluesselung. Alle Daten werden sicher uebertragen – das ist Pflicht fuer jede Website, die Nutzerdaten verarbeitet.',
    'Formulare ueber HTTPS': 'Deine Formulare werden sicher ueber HTTPS uebertragen. Kontaktanfragen und Daten deiner Nutzer sind damit verschluesselt.',
    'Keine unsicheren HTTP-Ressourcen': 'Deine Website laedt alle Ressourcen (Bilder, Skripte, CSS) ueber HTTPS. Es gibt keine Mixed-Content-Warnungen.',
    'Kontaktdaten vollstaendig': 'Deine Kontaktdaten sind vollstaendig angegeben. Nutzer koennen dich problemlos erreichen, was Vertrauen schafft und rechtlich erforderlich ist.',
    'Verarbeitungszwecke angegeben': 'Du hast die Verarbeitungszwecke in deiner Datenschutzerklaerung angegeben. Das ist eine Kernpflicht aus DSGVO Art. 13 Abs. 1 lit. c.',
    'Betroffenenrechte erwaehnt': 'Du informierst ueber die Rechte der Betroffenen (Auskunft, Loeschung, Widerspruch). Das ist gemaess DSGVO Art. 13 Abs. 2 lit. b Pflicht.',
    'Google Fonts lokal eingebunden': 'Deine Website laedt Google Fonts lokal statt ueber die Google-Server. Damit werden keine IP-Adressen an Google uebertragen – seit dem LG-Muenchen-Urteil (Az. 3 O 17493/20) ist das ein wichtiger Schutz vor Abmahnungen.',
    'Google Analytics/Tag Manager': 'Auf deiner Website wurde kein Google Analytics oder Tag Manager erkannt. Das vereinfacht die DSGVO-Konformitaet erheblich, da kein Tracking-Consent erforderlich ist.',
    'Meta/Facebook Pixel': 'Auf deiner Website wurde kein Meta/Facebook Pixel erkannt. Damit entfaellt die Pflicht, Nutzer ueber die Datenweitergabe an Meta zu informieren und eine Einwilligung einzuholen.',
    'YouTube datenschutzfreundlich': 'Deine YouTube-Einbettungen verwenden den erweiterten Datenschutzmodus (youtube-nocookie.com). Damit werden keine Cookies gesetzt, bevor der Nutzer das Video abspielt.',
    'Google Maps Einbettung': 'Auf deiner Website wurde keine direkte Google-Maps-Einbettung ohne Consent erkannt. Das ist positiv, da Maps ohne Einwilligung IP-Adressen an Google uebertraegt.',
    'Externe Chat-Tools': 'Auf deiner Website wurden keine externen Chat-Tools (Tawk.to, Intercom, HubSpot, Crisp) erkannt, die ohne Einwilligung Nutzerdaten an Drittanbieter uebertragen koennten.',
    'Social-Media-Plugins': 'Auf deiner Website wurden keine direkt eingebetteten Social-Media-Plugins (Facebook Like, Twitter Share) erkannt. Das ist gut, da diese ohne Einwilligung Nutzerdaten an die Plattformen uebertragen.',
    'Formular-Datenschutzhinweis': 'Deine Formulare enthalten einen Datenschutzhinweis. Nutzer werden informiert, wofuer ihre Daten verwendet werden – das ist gemaess DSGVO Art. 13 Pflicht.',
    'Datenschutzbeauftragter genannt': 'In deiner Datenschutzerklaerung ist ein Datenschutzbeauftragter benannt. Das ist ab 20 Mitarbeitern, die regelmaessig personenbezogene Daten verarbeiten, Pflicht (§38 BDSG).',
  };

  if (staerken.length > 0) {
    if (y > 220) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text('DEINE STAERKEN', 14, y);
    doc.setDrawColor(...hexToRgb('#16a34a'));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 65, y + 2);
    y += 8;

    staerken.forEach((s) => {
      if (y > 255) { doc.addPage(); y = 20; }
      const lobText = _staerkenLob[s.check] ?? `${s.check} ist korrekt umgesetzt.`;
      const lobLines = doc.splitTextToSize(lobText, pw - 46);
      const boxH = Math.max(18, 8 + lobLines.length * 4.5);

      doc.setFillColor(...hexToRgb('#16a34a'));
      doc.rect(14, y, 2, boxH, 'F');
      doc.setFillColor(240, 253, 244); // sehr helles Gruen
      doc.rect(16, y, pw - 30, boxH, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(s.check, 20, y + 5.5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(CI.gray));
      doc.text(`Dimension: ${s.dimension}`, 20, y + 10);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(22, 101, 52); // dunkelgruen
      doc.text(lobLines, 20, y + 15);

      y += boxH + 4;
    });
    y += 4;
  }

  // ── HANDLUNGSEMPFEHLUNGEN (nur fehlende Punkte) ──────────────────────────────
  const opts = d.optimierungen ?? [];
  if (opts.length > 0) {
    if (y > 220) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text('HANDLUNGSEMPFEHLUNGEN', 14, y);
    doc.setDrawColor(...hexToRgb(CI.red));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 90, y + 2);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...hexToRgb(CI.gray));
    doc.text('Folgende Punkte wurden bei der automatischen Pruefung nicht erkannt und sollten umgehend umgesetzt werden:', 14, y + 4);
    y += 10;

    opts.slice(0, 10).forEach((opt: any, i: number) => {
      if (y > 245) { doc.addPage(); y = 20; }
      const isKritisch = opt.prioritaet === 'kritisch';
      const isHoch = opt.prioritaet === 'hoch' || isKritisch;
      const accentColor = isKritisch ? CI.red : isHoch ? '#b45309' : CI.teal;
      const prioritaetLabel = isKritisch ? 'KRITISCH' : isHoch ? '!! HOCH' : '-- MITTEL';

      const optTitle = opt.titel ?? opt.check ?? opt.kriterium ?? '';
      const beschreibung = opt.beschreibung ?? opt.hinweis ?? opt.tipp ?? '';
      doc.setFontSize(7);
      const beschrLines = doc.splitTextToSize(beschreibung, pw - 46);

      const schritte: string[] = Array.isArray(opt.schritte) ? opt.schritte : [];
      const schritteLines: string[][] = schritte.map((s: string) => doc.splitTextToSize(`• ${s}`, pw - 50));
      const schritteLineCount = schritteLines.reduce((sum, l) => sum + l.length, 0);

      const beispiel = opt.beispiel ?? '';
      const beispielLines = beispiel ? doc.splitTextToSize(`Beispiel: ${beispiel}`, pw - 46) : [];

      const ergebnis = opt.erwartetes_ergebnis ?? '';
      const ergebnisLines = ergebnis ? doc.splitTextToSize(`Erwartetes Ergebnis: ${ergebnis}`, pw - 46) : [];

      const hasAufwand = opt.aufwand || opt.wirkung;
      const boxH =
        16 +
        (beschrLines.length * 3.5) +
        (schritteLineCount > 0 ? 6 + schritteLineCount * 3.5 : 0) +
        (beispielLines.length > 0 ? 5 + beispielLines.length * 3.5 : 0) +
        (ergebnisLines.length > 0 ? 5 + ergebnisLines.length * 3.5 : 0) +
        (hasAufwand ? 6 : 0);

      if (y + boxH > 270) { doc.addPage(); y = 20; }

      doc.setFillColor(...hexToRgb(accentColor));
      doc.rect(14, y, 2, boxH, 'F');
      doc.setFillColor(...hexToRgb(CI.lightGray));
      doc.rect(16, y, pw - 30, boxH, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`${i + 1}. ${optTitle}`, 20, y + 6);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(accentColor));
      doc.text(prioritaetLabel, pw - 16, y + 6, { align: 'right' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...hexToRgb(CI.gray));
      doc.text(`Dimension: ${opt.dimension ?? ''}`, 20, y + 11);

      let textY = y + 16;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(beschrLines, 20, textY);
      textY += beschrLines.length * 3.5;

      if (schritteLineCount > 0) {
        textY += 4;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.teal));
        doc.text('Schritt-für-Schritt:', 20, textY);
        textY += 4;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.black));
        schritteLines.forEach((lines) => { doc.text(lines, 22, textY); textY += lines.length * 3.5; });
      }

      if (beispielLines.length > 0) {
        textY += 3;
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb('#4f46e5'));
        doc.text(beispielLines, 20, textY);
        textY += beispielLines.length * 3.5;
      }

      if (ergebnisLines.length > 0) {
        textY += 3;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb('#16a34a'));
        doc.text(ergebnisLines, 20, textY);
        textY += ergebnisLines.length * 3.5;
      }

      if (hasAufwand) {
        textY += 2;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        const zeitInfo = opt.zeitaufwand ? ` | Zeit: ${opt.zeitaufwand}` : '';
        doc.text(`Aufwand: ${opt.aufwand ?? '\u2013'} | Wirkung: ${opt.wirkung ?? '\u2013'}${zeitInfo}`, 20, textY);
      }

      y += boxH + 5;
    });
  } else {
    // Alle Checks bestanden
    if (y > 250) { doc.addPage(); y = 20; }
    doc.setFillColor(240, 253, 244);
    doc.rect(14, y, pw - 28, 16, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(22, 101, 52);
    doc.text('Alle Datenschutz-Checks bestanden! Sehr gut.', pw / 2, y + 10, { align: 'center' });
    y += 22;
  }

  // Disclaimer
  if (y > 250) { doc.addPage(); y = 20; }
  y += 5;
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(14, y, pw - 28, 20, "F");
  doc.setFont("helvetica", "italic");
  doc.setFontSize(6.5);
  doc.setTextColor(...hexToRgb(CI.gray));
  const disclaimer = "Rechtlicher Hinweis: Dieser Report ist eine technische Bewertung und kein Ersatz für eine rechtliche Datenschutzberatung. Die Ergebnisse basieren auf automatisierten Prüfungen und können keine vollständige DSGVO-Konformität garantieren. Für eine rechtssichere Umsetzung empfehlen wir die Beratung durch einen Datenschutzbeauftragten oder Rechtsanwalt.";
  const disclaimerLines = doc.splitTextToSize(disclaimer, pw - 36);
  doc.text(disclaimerLines, 20, y + 6);
  y += 26;

  // CTA
  doc.setFillColor(...hexToRgb(CI.teal));
  doc.rect(14, y, pw - 28, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text("DSGVO-Konformität professionell umsetzen?", pw / 2, y + 9, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Kontaktiere uns: werbeagentur.me · Dein Schlüssel zum Marketingerfolg", pw / 2, y + 16, { align: "center" });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); addFooter(doc, i, total); }
  const domain = (d.url ?? d.domain ?? "website").replace(/https?:\/\//, "").replace(/\//g, "").slice(0, 30);
  doc.save(`Datenschutz-Barometer_${domain}.pdf`);
}

// ─── Conversion-Barometer PDF ─────────────────────────────────────────────────
// data: { url, domain, total_score, max_score, percentage, grade, branche,
//         dimensions: [{name, icon, score, max, findings, recommendations}],
//         top_recommendations: [{dimension, priority, text}],
//         meta: { load_time_ms, domain, title } }

export function generateConversionPdf(data: Record<string, unknown>) {
  const d = data as any;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();

  addHeader(doc, "CONVERSION-BAROMETER™", "Conversion-Rate-Optimierung (CRO)", d.url || d.meta?.domain || "");
  let y = 68;

  const sc = d.total_score ?? 0;
  const maxSc = d.max_score ?? 100;
  const pct = d.percentage ?? (maxSc > 0 ? Math.round((sc / maxSc) * 100) : 0);
  const grade = d.grade ?? "–";
  const scColor = scoreColor(pct);
  const amber = "#b45309";

  // Score-Box
  doc.setFillColor(...hexToRgb(CI.black));
  doc.rect(14, y, 80, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`${sc}`, 54, y + 22, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  doc.text(`von ${maxSc} Punkten`, 54, y + 30, { align: "center" });

  // Note + Branche
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(100, y, pw - 114, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`Note: ${grade}`, 106, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...hexToRgb(CI.gray));
  const noteText = pct >= 70 ? "Gute Conversion-Optimierung. Feinschliff empfohlen." : pct >= 45 ? "Ausbaufähiges Potenzial. Handlungsbedarf vorhanden." : "Schwache Conversion-Signale. Sofortmaßnahmen nötig.";
  doc.text(noteText, 106, y + 24);
  if (d.branche) {
    doc.setFontSize(6.5);
    doc.text(`Branche: ${d.branche}`, 106, y + 32);
  }
  y += 50;

  // Ladezeit
  if (d.meta?.load_time_ms) {
    const loadMs = d.meta.load_time_ms;
    const ltColor = loadMs <= 2000 ? "#16a34a" : loadMs <= 4000 ? "#ca8a04" : "#dc2626";
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("Ladezeit (Mobile):", 14, y + 3.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...hexToRgb(ltColor));
    doc.text(`${(loadMs / 1000).toFixed(1)}s`, pw - 14, y + 3.5, { align: "right" });
    y += 10;
  }

  // Branchenkontext-Hinweis
  if (d.branche) {
    doc.setFillColor(255, 251, 235);
    doc.rect(14, y, pw - 28, 14, "F");
    doc.setFont("helvetica", "italic");
    doc.setFontSize(6.5);
    doc.setTextColor(...hexToRgb(amber));
    const branchenText = `Branchenkontext: Conversion-Benchmarks variieren je Branche. E-Commerce: 1–3%, SaaS: 3–5%, Dienstleister: bis 10%. Branchenspezifische Besonderheiten können den Score beeinflussen.`;
    const branchenLines = doc.splitTextToSize(branchenText, pw - 36);
    doc.text(branchenLines, 20, y + 5);
    y += 18;
  }

  // Dimensionen
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(CI.black));
  doc.text("8 CONVERSION-DIMENSIONEN", 14, y);
  doc.setDrawColor(...hexToRgb(CI.red));
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 80, y + 2);
  y += 8;

  const dimensions = d.dimensions ?? [];
  dimensions.forEach((dim: any) => {
    if (y > 255) { doc.addPage(); y = 20; }
    const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
    const col = scoreColor(dp);

    doc.setFillColor(...hexToRgb(CI.black));
    doc.rect(14, y, pw - 28, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.white));
    // Nur den Namen anzeigen, kein Icon-URL als Text
    const dimLabel = String(dim.name ?? "").replace(/^\/manus-storage\/[^\s]+\s*/g, "");
    doc.text(dimLabel, 17, y + 5.5);
    doc.setTextColor(...hexToRgb(col));
    doc.text(`${dim.score}/${dim.max} (${dp}%)`, pw - 17, y + 5.5, { align: "right" });
    y += 8;
    drawProgressBar(doc, 14, y, pw - 28, 3, dp, col);
    y += 7;

    // Positiv-Findings
    (dim.findings ?? []).slice(0, 3).forEach((f: string) => {
      if (y > 265) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb("#16a34a"));
      doc.text("+", 18, y + 3.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(f, 24, y + 3.5);
      y += 5.5;
    });
    y += 3;
  });

  // Top-Empfehlungen
  const recs = d.top_recommendations ?? [];
  if (recs.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("TOP-EMPFEHLUNGEN FÜR MEHR CONVERSIONS", 14, y);
    doc.setDrawColor(...hexToRgb(CI.red));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 110, y + 2);
    y += 10;

    recs.slice(0, 10).forEach((rec: any, i: number) => {
      const isHoch = rec.priority === "hoch" || rec.prioritaet === "hoch" || rec.prioritaet === "kritisch";
      const barColor = isHoch ? amber : CI.gray;
      const prioritaetLabel = isHoch ? "!! HOCH" : "-- MITTEL";

      doc.setFontSize(7);
      const titel = rec.titel ?? rec.kriterium ?? rec.text ?? "";
      // Fallback: wenn keine Beschreibung vorhanden, aus Titel generieren
      // rec.text ist der Titel - NICHT als Beschreibung verwenden (wäre Duplikat)
      const beschreibungRaw = rec.beschreibung ?? rec.empfehlung ?? "";
      // Nur als Fallback wenn beschreibung wirklich leer oder identisch mit Titel ist
      const isBeschrDuplikat = beschreibungRaw.trim() === titel.trim();
      const beschreibung = (beschreibungRaw && !isBeschrDuplikat)
        ? beschreibungRaw
        : `${titel} ist ein wichtiger Hebel für mehr Conversions. Besucher, die auf dieser Seite landen, finden aktuell nicht das, was sie benötigen, um eine Entscheidung zu treffen. Durch gezielte Optimierung dieses Elements kannst du die Absprungrate senken und mehr Interessenten in Kunden umwandeln.`;
      const beschrLines = doc.splitTextToSize(beschreibung, pw - 42);

      const schritte: string[] = Array.isArray(rec.schritte) ? rec.schritte : [];
      const schritteLines: string[][] = schritte.map((s: string) => doc.splitTextToSize(`• ${s}`, pw - 46));
      const schritteLineCount = schritteLines.reduce((sum, l) => sum + l.length, 0);

      const beispiel = rec.beispiel ?? "";
      const beispielLines = beispiel ? doc.splitTextToSize(`Beispiel: ${beispiel}`, pw - 42) : [];

      const ergebnis = rec.erwartetes_ergebnis ?? "";
      const ergebnisLines = ergebnis ? doc.splitTextToSize(`Erwartetes Ergebnis: ${ergebnis}`, pw - 42) : [];

      const hasAufwand = rec.aufwand || rec.wirkung;
      const blockHeight =
        16 +
        (beschrLines.length * 3.5) +
        (schritteLineCount > 0 ? 6 + schritteLineCount * 3.5 : 0) +
        (beispielLines.length > 0 ? 5 + beispielLines.length * 3.5 : 0) +
        (ergebnisLines.length > 0 ? 5 + ergebnisLines.length * 3.5 : 0) +
        (hasAufwand ? 6 : 0);

      if (y + blockHeight > 270) { doc.addPage(); y = 20; }

      doc.setFillColor(...hexToRgb(barColor));
      doc.rect(14, y, 2, blockHeight, "F");
      doc.setFillColor(...hexToRgb(CI.lightGray));
      doc.rect(16, y, pw - 30, blockHeight, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`${i + 1}. ${titel}`, 20, y + 6);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(barColor));
      doc.text(prioritaetLabel, pw - 16, y + 6, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...hexToRgb(CI.gray));
      doc.text(`Dimension: ${rec.dimension ?? "\u2013"}`, 20, y + 11);

      let textY = y + 16;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(beschrLines, 20, textY);
      textY += beschrLines.length * 3.5;

      if (schritteLineCount > 0) {
        textY += 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.teal));
        doc.text("Schritt-für-Schritt:", 20, textY);
        textY += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.black));
        schritteLines.forEach((lines) => { doc.text(lines, 22, textY); textY += lines.length * 3.5; });
      }

      if (beispielLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#4f46e5"));
        doc.text(beispielLines, 20, textY);
        textY += beispielLines.length * 3.5;
      }

      if (ergebnisLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#16a34a"));
        doc.text(ergebnisLines, 20, textY);
        textY += ergebnisLines.length * 3.5;
      }

      if (hasAufwand) {
        textY += 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        const zeitInfo = rec.zeitaufwand ? ` | Zeit: ${rec.zeitaufwand}` : "";
        doc.text(`Aufwand: ${rec.aufwand ?? "\u2013"} | Wirkung: ${rec.wirkung ?? "\u2013"}${zeitInfo}`, 20, textY);
      }

      y += blockHeight + 4;
    });
  }

  // CTA
  if (y > 250) { doc.addPage(); y = 20; }
  y += 5;
  doc.setFillColor(...hexToRgb(amber));
  doc.rect(14, y, pw - 28, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text("Conversion-Hebel professionell umsetzen?", pw / 2, y + 9, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Kontaktiere uns: werbeagentur.me · Dein Schlüssel zum Marketingerfolg", pw / 2, y + 16, { align: "center" });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); addFooter(doc, i, total); }
  const domain = (d.url ?? d.meta?.domain ?? "website").replace(/https?:\/\//, "").replace(/\//g, "").slice(0, 30);
  doc.save(`Conversion-Barometer_${domain}.pdf`);
}

// ─── E-Commerce-Barometer PDF ─────────────────────────────────────────────────
// data: { url, domain, page_title, site_type, total_score, max_score,
//         reifegrad: { note, label, prozent, site_type_label },
//         scores: { [key]: { label, score, max } },
//         optimierungen: [{ dimension, kriterium, empfehlung, prioritaet }],
//         staerken: [{ dimension, text }] }

export function generateECommercePdf(data: Record<string, unknown>) {
  const d = data as any;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();

  const reifegrad = d.reifegrad ?? {};
  const pct = reifegrad.prozent ?? (d.max_score > 0 ? Math.round((d.total_score / d.max_score) * 100) : 0);
  const grade = reifegrad.note ?? "–";
  const gradeLabel = reifegrad.label ?? "";
  const siteTypeLabel = reifegrad.site_type_label ?? "";
  const scColor = scoreColor(pct);
  const ecomRed = "#6d0000";

  addHeader(doc, "E-COMMERCE-BAROMETER™", `${siteTypeLabel} · ${gradeLabel}`, d.url || d.domain || "");
  let y = 68;

  // Score-Box
  doc.setFillColor(...hexToRgb(CI.black));
  doc.rect(14, y, 80, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`${d.total_score ?? 0}`, 54, y + 22, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  doc.text(`von ${d.max_score ?? 120} Punkten`, 54, y + 30, { align: "center" });

  // Note + Seitentyp
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(100, y, pw - 114, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`Note: ${grade}`, 106, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...hexToRgb(CI.gray));
  doc.text(gradeLabel, 106, y + 24);
  doc.setFontSize(6.5);
  doc.text(`Erkannt als: ${siteTypeLabel}`, 106, y + 32);
  y += 50;

  // Dimensionen
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...hexToRgb(CI.black));
  doc.text("8 DIMENSIONEN IM ÜBERBLICK", 14, y);
  doc.setDrawColor(...hexToRgb(ecomRed));
  doc.setLineWidth(0.8);
  doc.line(14, y + 2, 80, y + 2);
  y += 8;

  const scores = d.scores ?? {};
  Object.entries(scores).forEach(([, dim]: [string, any]) => {
    if (y > 255) { doc.addPage(); y = 20; }
    const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
    const col = scoreColor(dp);

    doc.setFillColor(...hexToRgb(CI.black));
    doc.rect(14, y, pw - 28, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...hexToRgb(CI.white));
    doc.text(dim.label ?? "", 17, y + 5.5);
    doc.setTextColor(...hexToRgb(col));
    doc.text(`${dim.score}/${dim.max} (${dp}%)`, pw - 17, y + 5.5, { align: "right" });
    y += 8;
    drawProgressBar(doc, 14, y, pw - 28, 3, dp, col);
    y += 7;
  });

  // Stärken
  const staerken = d.staerken ?? [];
  if (staerken.length > 0) {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("STÄRKEN", 14, y);
    doc.setDrawColor(...hexToRgb("#16a34a"));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 40, y + 2);
    y += 8;
    staerken.slice(0, 6).forEach((s: any) => {
      if (y > 265) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb("#16a34a"));
      doc.text("+", 18, y + 3.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`${s.dimension}: ${s.text}`, 24, y + 3.5);
      y += 5.5;
    });
    y += 4;
  }

  // Optimierungen
  const opts = d.optimierungen ?? [];
  if (opts.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("OPTIMIERUNGSEMPFEHLUNGEN", 14, y);
    doc.setDrawColor(...hexToRgb(ecomRed));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 90, y + 2);
    y += 10;

    opts.slice(0, 10).forEach((opt: any, i: number) => {
      const isHoch = opt.prioritaet === "hoch" || opt.prioritaet === "kritisch";
      const barColor = isHoch ? ecomRed : (opt.prioritaet === "niedrig" ? "#4f46e5" : "#b45309");
      const prioritaetLabel = isHoch ? "!! HOCH" : (opt.prioritaet === "niedrig" ? "◦ NIEDRIG" : "-- MITTEL");

      doc.setFontSize(7);
      const beschreibung = opt.beschreibung ?? opt.empfehlung ?? opt.tipp ?? "";
      const beschrLines = doc.splitTextToSize(beschreibung, pw - 42);

      const schritte: string[] = Array.isArray(opt.schritte) ? opt.schritte : [];
      const schritteLines: string[][] = schritte.map((s: string) => doc.splitTextToSize(`• ${s}`, pw - 46));
      const schritteLineCount = schritteLines.reduce((sum, l) => sum + l.length, 0);

      const beispiel = opt.beispiel ?? "";
      const beispielLines = beispiel ? doc.splitTextToSize(`Beispiel: ${beispiel}`, pw - 42) : [];

      const ergebnis = opt.erwartetes_ergebnis ?? "";
      const ergebnisLines = ergebnis ? doc.splitTextToSize(`Erwartetes Ergebnis: ${ergebnis}`, pw - 42) : [];

      const hasAufwand = opt.aufwand || opt.wirkung;
      const blockHeight =
        16 +
        (beschrLines.length * 3.5) +
        (schritteLineCount > 0 ? 6 + schritteLineCount * 3.5 : 0) +
        (beispielLines.length > 0 ? 5 + beispielLines.length * 3.5 : 0) +
        (ergebnisLines.length > 0 ? 5 + ergebnisLines.length * 3.5 : 0) +
        (hasAufwand ? 6 : 0);

      if (y + blockHeight > 270) { doc.addPage(); y = 20; }

      doc.setFillColor(...hexToRgb(barColor));
      doc.rect(14, y, 2, blockHeight, "F");
      doc.setFillColor(...hexToRgb(CI.lightGray));
      doc.rect(16, y, pw - 30, blockHeight, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`${i + 1}. ${opt.titel ?? opt.kriterium ?? ""}`, 20, y + 6);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(barColor));
      doc.text(prioritaetLabel, pw - 16, y + 6, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...hexToRgb(CI.gray));
      doc.text(`Dimension: ${opt.dimension ?? "\u2013"}`, 20, y + 11);

      let textY = y + 16;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(beschrLines, 20, textY);
      textY += beschrLines.length * 3.5;

      if (schritteLineCount > 0) {
        textY += 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.teal));
        doc.text("Schritt-für-Schritt:", 20, textY);
        textY += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.black));
        schritteLines.forEach((lines) => { doc.text(lines, 22, textY); textY += lines.length * 3.5; });
      }

      if (beispielLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#4f46e5"));
        doc.text(beispielLines, 20, textY);
        textY += beispielLines.length * 3.5;
      }

      if (ergebnisLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#16a34a"));
        doc.text(ergebnisLines, 20, textY);
        textY += ergebnisLines.length * 3.5;
      }

      if (hasAufwand) {
        textY += 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        const zeitInfo = opt.zeitaufwand ? ` | Zeit: ${opt.zeitaufwand}` : "";
        doc.text(`Aufwand: ${opt.aufwand ?? "\u2013"} | Wirkung: ${opt.wirkung ?? "\u2013"}${zeitInfo}`, 20, textY);
      }

      y += blockHeight + 4;
    });
  }

  // CTA
  if (y > 250) { doc.addPage(); y = 20; }
  y += 5;
  doc.setFillColor(...hexToRgb(ecomRed));
  doc.rect(14, y, pw - 28, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text("E-Commerce professionell optimieren?", pw / 2, y + 9, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Kontaktiere uns: werbeagentur.me · Dein Schlüssel zum Marketingerfolg", pw / 2, y + 16, { align: "center" });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); addFooter(doc, i, total); }
  const domain = (d.url ?? d.domain ?? "website").replace(/https?:\/\//, "").replace(/\//g, "").slice(0, 30);
  doc.save(`ECommerce-Barometer_${domain}.pdf`);
}

// ─── Wettbewerbs-Barometer PDF ────────────────────────────────────────────────
// data: { url, domain, mode, own_rank, total_sites, competitors_found,
//         websites: [{ url, domain, title, total_score, max_score, percent, grade, scores, is_own, label }],
//         comparison: { [dim]: { own, avg, best, gap, advantage } },
//         own_staerken: [{ dimension, text }],
//         own_schwaechen: [{ dimension, text }],
//         optimierungen: [{ dimension, kriterium, empfehlung, prioritaet }] }

export function generateWettbewerbPdf(data: Record<string, unknown>) {
  const d = data as any;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();

  const websites = (d.websites ?? []) as any[];
  const ownSite = websites.find((w: any) => w.is_own) ?? websites[0] ?? {};
  const pct = ownSite.percent ?? 0;
  const grade = ownSite.grade ?? "–";
  const scColor = scoreColor(pct);
  const wettbColor = "#1e3a5f";

  addHeader(doc, "WETTBEWERBS-BAROMETER™", `Rang ${d.own_rank ?? "?"} von ${d.total_sites ?? "?"} analysierten Websites`, d.url || ownSite.url || "");
  let y = 68;

  // Score-Box
  doc.setFillColor(...hexToRgb(CI.black));
  doc.rect(14, y, 80, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`${ownSite.total_score ?? 0}`, 54, y + 22, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(180, 180, 180);
  doc.text(`von ${ownSite.max_score ?? 100} Punkten`, 54, y + 30, { align: "center" });

  // Note + Ranking
  doc.setFillColor(...hexToRgb(CI.lightGray));
  doc.rect(100, y, pw - 114, 40, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...hexToRgb(scColor));
  doc.text(`Note: ${grade}`, 106, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...hexToRgb(CI.gray));
  doc.text(`Rang ${d.own_rank ?? "?"} von ${d.total_sites ?? "?"} Websites`, 106, y + 24);
  doc.setFontSize(6.5);
  doc.text(`${d.competitors_found ?? 0} Wettbewerber analysiert`, 106, y + 32);
  y += 50;

  // Wettbewerber-Übersicht
  if (websites.length > 1) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("WETTBEWERBER-VERGLEICH", 14, y);
    doc.setDrawColor(...hexToRgb(wettbColor));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 80, y + 2);
    y += 8;

    websites.slice(0, 4).forEach((site: any) => {
      if (y > 255) { doc.addPage(); y = 20; }
      const sp = site.percent ?? 0;
      const sc = scoreColor(sp);
      doc.setFillColor(...hexToRgb(site.is_own ? CI.black : CI.lightGray));
      doc.rect(14, y, pw - 28, 8, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...hexToRgb(site.is_own ? CI.white : CI.black));
      doc.text(`${site.is_own ? "▶ " : ""}${site.label ?? site.domain ?? site.url}`, 17, y + 5.5);
      doc.setTextColor(...hexToRgb(sc));
      doc.text(`${site.total_score}/${site.max_score} (${sp}%) · Note ${site.grade}`, pw - 17, y + 5.5, { align: "right" });
      y += 8;
      drawProgressBar(doc, 14, y, pw - 28, 3, sp, sc);
      y += 7;
    });
    y += 4;
  }

  // Stärken & Schwächen
  const staerken = d.own_staerken ?? [];
  const schwaechen = d.own_schwaechen ?? [];
  if (staerken.length > 0 || schwaechen.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    const colW = (pw - 32) / 2;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...hexToRgb("#16a34a"));
    doc.text("STÄRKEN", 14, y);
    doc.setTextColor(...hexToRgb("#dc2626"));
    doc.text("SCHWÄCHEN", 14 + colW + 4, y);
    y += 6;

    const maxRows = Math.max(staerken.length, schwaechen.length);
    for (let i = 0; i < Math.min(maxRows, 4); i++) {
      if (y > 265) { doc.addPage(); y = 20; }
      if (staerken[i]) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb("#16a34a"));
        doc.text("+", 14, y + 3.5);
        doc.setTextColor(...hexToRgb(CI.black));
        doc.text(`${staerken[i].dimension}: ${staerken[i].text}`, 20, y + 3.5, { maxWidth: colW - 10 });
      }
      if (schwaechen[i]) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb("#dc2626"));
        doc.text("x", 14 + colW + 4, y + 3.5);
        doc.setTextColor(...hexToRgb(CI.black));
        doc.text(`${schwaechen[i].dimension}: ${schwaechen[i].text}`, 14 + colW + 10, y + 3.5, { maxWidth: colW - 10 });
      }
      y += 7;
    }
    y += 4;
  }

  // Optimierungen
  const opts = d.optimierungen ?? [];
  if (opts.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...hexToRgb(CI.black));
    doc.text("WETTBEWERBS-OPTIMIERUNGEN", 14, y);
    doc.setDrawColor(...hexToRgb(wettbColor));
    doc.setLineWidth(0.8);
    doc.line(14, y + 2, 95, y + 2);
    y += 10;

    opts.slice(0, 10).forEach((opt: any, i: number) => {
      const isHoch = opt.prioritaet === "hoch" || opt.prioritaet === "kritisch";
      const barColor = isHoch ? wettbColor : (opt.prioritaet === "niedrig" ? "#4f46e5" : "#b45309");
      const prioritaetLabel = isHoch ? "!! HOCH" : (opt.prioritaet === "niedrig" ? "◦ NIEDRIG" : "-- MITTEL");

      doc.setFontSize(7);
      const beschreibung = opt.beschreibung ?? opt.empfehlung ?? opt.tipp ?? "";
      const beschrLines = doc.splitTextToSize(beschreibung, pw - 42);

      const schritte: string[] = Array.isArray(opt.schritte) ? opt.schritte : [];
      const schritteLines: string[][] = schritte.map((s: string) => doc.splitTextToSize(`• ${s}`, pw - 46));
      const schritteLineCount = schritteLines.reduce((sum, l) => sum + l.length, 0);

      const beispiel = opt.beispiel ?? "";
      const beispielLines = beispiel ? doc.splitTextToSize(`Beispiel: ${beispiel}`, pw - 42) : [];

      const ergebnis = opt.erwartetes_ergebnis ?? "";
      const ergebnisLines = ergebnis ? doc.splitTextToSize(`Erwartetes Ergebnis: ${ergebnis}`, pw - 42) : [];

      const hasAufwand = opt.aufwand || opt.wirkung;
      const blockHeight =
        16 +
        (beschrLines.length * 3.5) +
        (schritteLineCount > 0 ? 6 + schritteLineCount * 3.5 : 0) +
        (beispielLines.length > 0 ? 5 + beispielLines.length * 3.5 : 0) +
        (ergebnisLines.length > 0 ? 5 + ergebnisLines.length * 3.5 : 0) +
        (hasAufwand ? 6 : 0);

      if (y + blockHeight > 270) { doc.addPage(); y = 20; }

      doc.setFillColor(...hexToRgb(barColor));
      doc.rect(14, y, 2, blockHeight, "F");
      doc.setFillColor(...hexToRgb(CI.lightGray));
      doc.rect(16, y, pw - 30, blockHeight, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(`${i + 1}. ${opt.titel ?? opt.kriterium ?? ""}`, 20, y + 6);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(barColor));
      doc.text(prioritaetLabel, pw - 16, y + 6, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...hexToRgb(CI.gray));
      doc.text(`Dimension: ${opt.dimension ?? "\u2013"}`, 20, y + 11);

      let textY = y + 16;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...hexToRgb(CI.black));
      doc.text(beschrLines, 20, textY);
      textY += beschrLines.length * 3.5;

      if (schritteLineCount > 0) {
        textY += 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.teal));
        doc.text("Schritt-für-Schritt:", 20, textY);
        textY += 4;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(...hexToRgb(CI.black));
        schritteLines.forEach((lines) => { doc.text(lines, 22, textY); textY += lines.length * 3.5; });
      }

      if (beispielLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "italic");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#4f46e5"));
        doc.text(beispielLines, 20, textY);
        textY += beispielLines.length * 3.5;
      }

      if (ergebnisLines.length > 0) {
        textY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb("#16a34a"));
        doc.text(ergebnisLines, 20, textY);
        textY += ergebnisLines.length * 3.5;
      }

      if (hasAufwand) {
        textY += 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(...hexToRgb(CI.gray));
        const zeitInfo = opt.zeitaufwand ? ` | Zeit: ${opt.zeitaufwand}` : "";
        doc.text(`Aufwand: ${opt.aufwand ?? "\u2013"} | Wirkung: ${opt.wirkung ?? "\u2013"}${zeitInfo}`, 20, textY);
      }

      y += blockHeight + 4;
    });
  }

  // CTA
  if (y > 250) { doc.addPage(); y = 20; }
  y += 5;
  doc.setFillColor(...hexToRgb(wettbColor));
  doc.rect(14, y, pw - 28, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...hexToRgb(CI.white));
  doc.text("Wettbewerbsvorteile professionell ausbauen?", pw / 2, y + 9, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Kontaktiere uns: werbeagentur.me · Dein Schlüssel zum Marketingerfolg", pw / 2, y + 16, { align: "center" });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) { doc.setPage(i); addFooter(doc, i, total); }
  const domain = (d.url ?? ownSite.url ?? "website").replace(/https?:\/\//, "").replace(/\//g, "").slice(0, 30);
  doc.save(`Wettbewerbs-Barometer_${domain}.pdf`);
}
